import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

export type StudioActionName = 'onPdfExport' | 'onExcelExport' | 'onSave' | 'onSaveAs' | 'setAfterUnsavedChangesAction';

export type ActionInterceptor = (actionName: StudioActionName) => Promise<boolean>;

const StudioActionInterceptorContext = createContext<React.MutableRefObject<Set<ActionInterceptor>> | undefined>(
  undefined,
);
StudioActionInterceptorContext.displayName = 'StudioActionInterceptorContext';

export const StudioActionInterceptorProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const registryRef = useRef(new Set<ActionInterceptor>());
  return (
    <StudioActionInterceptorContext.Provider value={registryRef}>{children}</StudioActionInterceptorContext.Provider>
  );
};

export const useRunStudioActionInterceptors = (): ((actionName: StudioActionName) => Promise<boolean>) => {
  const registryRef = useContext(StudioActionInterceptorContext);

  return useCallback(
    async (actionName: StudioActionName) => {
      if (!registryRef?.current?.size) {
        return true;
      }

      for (const interceptor of registryRef.current) {
        // eslint-disable-next-line no-await-in-loop
        const isInterceptorPassed = await interceptor(actionName);
        if (!isInterceptorPassed) {
          return false;
        }
      }

      return true;
    },
    [registryRef],
  );
};

export const useRegisterStudioActionInterceptor = (interceptor: ActionInterceptor): void => {
  const registryRef = useContext(StudioActionInterceptorContext);
  const interceptorRef = useRef(interceptor);
  interceptorRef.current = interceptor;

  useEffect(() => {
    if (!registryRef) {
      return undefined;
    }

    const registry = registryRef.current;
    const stableInterceptor: ActionInterceptor = (actionName) => interceptorRef.current(actionName);
    registry.add(stableInterceptor);

    return () => {
      registry.delete(stableInterceptor);
    };
  }, [registryRef]);
};
